/**
 * Electrochemistry: the Nernst equation, ΔG = −nFE, and the standard cell potential.
 *
 *   E = E° − (RT / nF) ln Q        ΔG = −nFE        ΔG° = −nFE° = −RT ln K
 *
 * R = 8.314 462 618 J/(mol·K) and F = 96 485.332 12 C/mol are exact (2019 SI). Q and K
 * are carried as natural logs so a huge K reports a power of ten instead of overflowing.
 */

import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";
import { FARADAY, GAS_CONSTANT } from "./constants";
import { type HeatFailure, type ReadSpec, readAll } from "./heat";
import { fromKelvin } from "./units";

export const VOLTAGE_UNITS = [
  { id: "V", symbol: "V", factor: 1 },
  { id: "mV", symbol: "mV", factor: 1e-3 },
] as const;

export const FREE_ENERGY_UNITS = [
  { id: "kJ", symbol: "kJ/mol", factor: 1000 },
  { id: "J", symbol: "J/mol", factor: 1 },
] as const;

export type ElectroFailure =
  | HeatFailure
  | { ok: false; reason: "badN" | "zeroLogQ" | "signMismatch" | "zeroValue" };

interface Unit {
  id: string;
  factor: number;
}

function factorOf(units: readonly Unit[], id: string): number {
  return (units.find((u) => u.id === id) ?? units[0]).factor;
}

/** The electron count: a whole number from 1 up. */
export function readElectrons(text: string): { n: number } | ElectroFailure {
  if (text.trim() === "") {
    return { ok: false, reason: "missing" };
  }
  const typed = parseMeasuredNumber(text);
  if (typed === null) {
    return { ok: false, reason: "unreadable", key: "n" };
  }
  return Number.isInteger(typed) && typed >= 1
    ? { n: typed }
    : { ok: false, reason: "badN" };
}

/* ---------------------------------------------------- Nernst equation */

export type NernstTerm = "e" | "e0" | "q" | "t";

export interface NernstInput {
  unknown: NernstTerm;
  n: string;
  text: Record<NernstTerm, string>;
  unit: { e: string; e0: string; t: string };
}

export interface NernstSolution {
  ok: true;
  unknown: NernstTerm;
  n: number;
  /** V, V, K */
  e: number;
  e0: number;
  kelvin: number;
  lnQ: number;
  log10Q: number;
  /** Q itself, or null when it overflows a double. */
  q: number | null;
  /** RT/nF in volts; the Nernst slope per ln unit. */
  slope: number;
  /** ΔG = −nFE and ΔG° = −nFE°, J/mol. */
  dg: number;
  dg0: number;
  /** log₁₀ K = nFE° / (RT ln 10) */
  log10K: number;
  answer: number;
  answerUnit: string;
}

export type NernstOutcome = NernstSolution | ElectroFailure;

const NERNST_KINDS = {
  e: "any",
  e0: "any",
  q: "positive",
  t: "temperature",
} as const;

/** E, E°, ln Q and T once the unknown has been found. */
function nernstValues(
  unknown: NernstTerm,
  n: number,
  v: Record<string, number>
): { e: number; e0: number; t: number; lnQ: number } | ElectroFailure {
  const nF = n * FARADAY;
  const known = {
    e: v.e,
    e0: v.e0,
    t: v.t,
    lnQ: v.q === undefined ? 0 : Math.log(v.q),
  };
  if (unknown === "e") {
    known.e = known.e0 - ((GAS_CONSTANT * known.t) / nF) * known.lnQ;
  } else if (unknown === "e0") {
    known.e0 = known.e + ((GAS_CONSTANT * known.t) / nF) * known.lnQ;
  } else if (unknown === "q") {
    known.lnQ = ((known.e0 - known.e) * nF) / (GAS_CONSTANT * known.t);
  } else {
    if (known.lnQ === 0) {
      return { ok: false, reason: "zeroLogQ" };
    }
    known.t = ((known.e0 - known.e) * nF) / (GAS_CONSTANT * known.lnQ);
    if (!(known.t > 0)) {
      return { ok: false, reason: "resultBelowZero" };
    }
  }
  return known;
}

export function solveNernst(input: NernstInput): NernstOutcome {
  const { unknown, unit } = input;
  const nRead = readElectrons(input.n);
  if ("ok" in nRead) {
    return nRead;
  }
  const specs: ReadSpec[] = (["e", "e0", "q", "t"] as const)
    .filter((term) => term !== unknown)
    .map((term) => ({
      key: term,
      text: input.text[term],
      kind: NERNST_KINDS[term],
      unit: term === "t" ? unit.t : "",
      factor:
        term === "e" || term === "e0" ? factorOf(VOLTAGE_UNITS, unit[term]) : 1,
    }));
  const read = readAll(specs);
  if ("ok" in read) {
    return read;
  }
  const n = nRead.n;
  const nF = n * FARADAY;
  const solved = nernstValues(unknown, n, read.values);
  if ("ok" in solved) {
    return solved;
  }
  const { e, e0, t, lnQ } = solved;
  const slope = (GAS_CONSTANT * t) / nF;
  const qValue = Math.exp(lnQ);
  let answer: number;
  if (unknown === "e" || unknown === "e0") {
    answer =
      (unknown === "e" ? e : e0) / factorOf(VOLTAGE_UNITS, unit[unknown]);
  } else if (unknown === "q") {
    answer = qValue;
  } else {
    answer = fromKelvin(t, unit.t);
  }
  return {
    ok: true,
    unknown,
    n,
    e,
    e0,
    kelvin: t,
    lnQ,
    log10Q: lnQ / Math.LN10,
    q: Number.isFinite(qValue) && qValue > 0 ? qValue : null,
    slope,
    dg: -nF * e,
    dg0: -nF * e0,
    log10K: (nF * e0) / (GAS_CONSTANT * t * Math.LN10),
    answer,
    answerUnit: unknown === "q" ? "" : unit[unknown === "t" ? "t" : unknown],
  };
}

/* ------------------------------------------------------- ΔG = −nFE */

export type FreeEnergyTerm = "g" | "n" | "e";

export interface FreeEnergyInput {
  unknown: FreeEnergyTerm;
  text: { g: string; n: string; e: string };
  unit: { g: string; e: string };
}

export interface FreeEnergySolution {
  ok: true;
  unknown: FreeEnergyTerm;
  /** J/mol, electrons, V */
  g: number;
  n: number;
  e: number;
  answer: number;
  answerUnit: string;
  /** Solved n is not a whole number. */
  fractionalN: boolean;
}

export type FreeEnergyOutcome = FreeEnergySolution | ElectroFailure;

export function solveFreeEnergy(input: FreeEnergyInput): FreeEnergyOutcome {
  const { unknown, unit } = input;
  const specs: ReadSpec[] = [];
  if (unknown !== "g") {
    specs.push({
      key: "g",
      text: input.text.g,
      kind: "any",
      unit: "",
      factor: factorOf(FREE_ENERGY_UNITS, unit.g),
    });
  }
  if (unknown !== "e") {
    specs.push({
      key: "e",
      text: input.text.e,
      kind: "any",
      unit: "",
      factor: factorOf(VOLTAGE_UNITS, unit.e),
    });
  }
  if (unknown !== "n") {
    const nRead = readElectrons(input.text.n);
    if ("ok" in nRead) {
      return nRead;
    }
  }
  const read = readAll(specs);
  if ("ok" in read) {
    return read;
  }
  const nTyped =
    unknown === "n" ? 0 : (readElectrons(input.text.n) as { n: number }).n;
  const v = read.values;
  let g = v.g;
  let e = v.e;
  let n = nTyped;
  if (unknown === "g") {
    g = -n * FARADAY * e;
  } else if (unknown === "e") {
    e = -g / (n * FARADAY);
  } else {
    if (e === 0 || g === 0) {
      return { ok: false, reason: "zeroValue" };
    }
    n = -g / (FARADAY * e);
    if (!(n > 0)) {
      return { ok: false, reason: "signMismatch" };
    }
  }
  const answers = {
    g: g / factorOf(FREE_ENERGY_UNITS, unit.g),
    e: e / factorOf(VOLTAGE_UNITS, unit.e),
    n,
  };
  return {
    ok: true,
    unknown,
    g,
    n,
    e,
    answer: answers[unknown],
    answerUnit: unknown === "n" ? "" : unit[unknown],
    fractionalN: unknown === "n" && Math.abs(n - Math.round(n)) > 0.05,
  };
}
